import React from 'react';
import { useSelector } from 'react-redux';
import { useAppContext } from 'src/components/app-context';
import mergeWith from 'lodash/mergeWith';

import { OfferListFilterContextContainer } from './components/offer-list-filter-context';

import { ProductPage } from './product-page';
import FeedbackButton from '../feedback/feedback-button';
import { readPersonalizationSettings } from '../user/storage/personalization';

import productPageQuery from './query.graphql';

import type { AppState } from 'src/reducers';

import { PRODUCT_CONTEXT_ID } from './constants';

import type { Campaign, ProductPageQuery } from 'src/types';
import type { Variables, Loaded, Group } from './product-types';
import type { ProductPageData, RouteSection } from './types';

type Props = {
  activeCampaign: Campaign;
  data: ProductPageData;
  routeSection: RouteSection;
};

const Product = (props: Props) => {
  const { activeCampaign, data, routeSection } = props;

  const { t } = useAppContext();

  const me = useSelector((state: AppState) => state.areas.account.me);

  if (!data.product) {
    return null;
  }

  return (
    <OfferListFilterContextContainer>
      {me && me.analyticsAccess && (
        <FeedbackButton>{t('feedback', 'button.text')}</FeedbackButton>
      )}
      <ProductPage
        activeCampaign={activeCampaign}
        data={data}
        routeSection={routeSection}
      />
    </OfferListFilterContextContainer>
  );
};

Product.graphQL = productPageQuery;

// Group on product ID, ignore section param
Product.group = ({ name, match }: Group) =>
  `${name}_${match && match.params ? match.params.id : 'unknown'}`;

Product.variables = ({ id, offset, section, marketCode }: Variables) => {
  const expertsEnabled = ['se'].includes(marketCode);
  return {
    id: parseInt(id, 10),
    offset: parseInt(offset, 10) || 0,
    section,
    statisticsTime: section === 'statistics' ? '1970-01-02' : undefined,
    marketCode,
    personalizationExcludeCategories:
      readPersonalizationSettings().notInterestedIn.categories.map(
        (category: { id: string }) => +category.id
      ),
    recommendationsContextId: PRODUCT_CONTEXT_ID,
    advized: expertsEnabled,
  };
};

Product.loadOrder = (
  { section = 'main' } = {},
  loaded = {} as Loaded,
  options = { initialRoute: false }
) => {
  const sectionContent = {
    main: {
      priceList: true,
    },
    properties: {
      productProperties: true,
    },
    reviews: {
      userReviews: true,
    },
    statistics: {
      priceHistory: true,
      importantDates: true,
      priceHistoryTable: true,
      priceTrend: true,
      medianPrice: true,
    },
  };

  return [
    {
      main: !loaded.main,
      ...sectionContent[section],
    },
    {
      // Refresh price list for cached pages only
      priceList: options.initialRoute && section === 'main',
      userActions: !loaded.userActions,
      badges: !loaded.badges,
      media: !loaded.media,
      campaign: !loaded.campaign,
      relatedProducts: !loaded.relatedProducts,
      campaignDeals: !loaded.campaignDeals,
      priceHistory: !loaded.priceHistory,
    },
  ];
};

Product.mergeData = (oldData: ProductPageQuery, newData: ProductPageQuery) => {
  const data = mergeWith({}, oldData, newData);

  // If price data was refetched always use data from latest fetch
  if (newData?.product?.prices) {
    return {
      ...data,
      product: {
        ...data.product,
        prices: newData.product.prices,
      },
    };
  }

  return data;
};

export default Product;
